import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  getDoc,
  db
} from '../../utils/database/firebase-config';

import { auditService, AUDIT_MODULES, AUDIT_ACTIONS, AUDIT_TAGS } from '../system/audit';

const COLLECTION_NAME = 'externalDrivers';

const extDriverLabel = (d) => d?.name || d?.phone || d?.id || 'external driver';

export const externalDriversService = {
  // Get all external drivers for a company
  async getExternalDrivers(companyId) {
    try {
      console.log('Fetching external drivers for company:', companyId);
      
      // First try without orderBy to avoid index issues
      const q = query(
        collection(db, COLLECTION_NAME),
        where('companyId', '==', companyId)
      );
      const querySnapshot = await getDocs(q);
      const drivers = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort in memory to avoid Firestore index requirements
      drivers.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      
      console.log('Found external drivers:', drivers.length);
      return drivers;
    } catch (error) {
      console.error('Error fetching external drivers:', error);
      console.error('Error details:', error.message);
      throw error;
    }
  },

  // Find external driver by phone number
  async findDriverByPhone(companyId, phone) {
    try {
      const q = query(
        collection(db, COLLECTION_NAME),
        where('companyId', '==', companyId),
        where('phone', '==', phone)
      );
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error finding driver by phone:', error);
      throw error;
    }
  },

  // Add or update external driver
  async addOrUpdateExternalDriver(companyId, driverData) {
    try {
      // Check if driver exists by phone
      const existingDriver = await this.findDriverByPhone(companyId, driverData.phone);
      
      if (existingDriver) {
        // Update existing driver
        const driverRef = doc(db, COLLECTION_NAME, existingDriver.id);
        await updateDoc(driverRef, {
          name: driverData.name,
          updatedAt: new Date()
        });
        return existingDriver.id;
      } else {
        // Create new driver
        const newDriver = {
          companyId,
          name: driverData.name,
          phone: driverData.phone,
          totalShipments: 0,
          totalEarnings: 0,
          createdAt: new Date(),
          updatedAt: new Date()
        };
        const docRef = await addDoc(collection(db, COLLECTION_NAME), newDriver);
        auditService.log({
          module: AUDIT_MODULES.EXTERNAL_DRIVER,
          action: AUDIT_ACTIONS.CREATE,
          recordId: docRef.id,
          recordLabel: extDriverLabel(newDriver),
          after: newDriver,
          tags: [AUDIT_TAGS.ALL, AUDIT_TAGS.DRIVER_CHANGES],
          companyId,
          meta: { driverId: docRef.id, external: true }
        });
        return docRef.id;
      }
    } catch (error) {
      console.error('Error adding/updating external driver:', error);
      throw error;
    }
  },

  // Get external driver by ID
  async getExternalDriver(driverId) {
    try {
      const docRef = doc(db, COLLECTION_NAME, driverId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error fetching external driver:', error);
      throw error;
    }
  },

  // Update driver stats after shipment
  async updateDriverStats(driverId, shipmentPrice) {
    try {
      const driverRef = doc(db, COLLECTION_NAME, driverId);
      const driverDoc = await getDoc(driverRef);
      
      if (driverDoc.exists()) {
        const currentData = driverDoc.data();
        const newTotalShipments = (currentData.totalShipments || 0) + 1;
        const newTotalEarnings = (currentData.totalEarnings || 0) + parseFloat(shipmentPrice);
        await updateDoc(driverRef, {
          totalShipments: newTotalShipments,
          totalEarnings: newTotalEarnings,
          updatedAt: new Date()
        });
        auditService.log({
          module: AUDIT_MODULES.EXTERNAL_DRIVER_LEDGER,
          action: AUDIT_ACTIONS.LEDGER_ADJUSTMENT,
          recordId: driverId,
          recordLabel: extDriverLabel(currentData),
          before: { totalShipments: currentData.totalShipments || 0, totalEarnings: currentData.totalEarnings || 0 },
          after: { totalShipments: newTotalShipments, totalEarnings: newTotalEarnings },
          tags: [AUDIT_TAGS.ALL, AUDIT_TAGS.FINANCIAL, AUDIT_TAGS.DRIVER_CHANGES],
          companyId: currentData.companyId,
          meta: { driverId, shipmentPriceDelta: parseFloat(shipmentPrice) }
        });
      }
    } catch (error) {
      console.error('Error updating driver stats:', error);
      throw error;
    }
  },

  // Update external driver name/phone
  async updateExternalDriver(driverId, updates) {
    try {
      const ref = doc(db, COLLECTION_NAME, driverId);
      let before = null;
      try {
        const snap = await getDoc(ref);
        if (snap.exists()) before = { id: snap.id, ...snap.data() };
      } catch { /* best effort */ }

      const after = {
        ...(updates.name !== undefined ? { name: updates.name } : {}),
        ...(updates.phone !== undefined ? { phone: updates.phone } : {}),
        updatedAt: new Date()
      };
      await updateDoc(ref, after);

      auditService.log({
        module: AUDIT_MODULES.EXTERNAL_DRIVER,
        action: AUDIT_ACTIONS.UPDATE,
        recordId: driverId,
        recordLabel: extDriverLabel({ ...before, ...after }),
        before,
        after: { ...(before || {}), ...after },
        tags: [AUDIT_TAGS.ALL, AUDIT_TAGS.DRIVER_CHANGES],
        companyId: before?.companyId,
        meta: { driverId, external: true }
      });
    } catch (error) {
      console.error('Error updating external driver:', error);
      throw error;
    }
  },

  // Delete external driver
  async deleteExternalDriver(driverId) {
    try {
      const ref = doc(db, COLLECTION_NAME, driverId);
      let before = null;
      try {
        const snap = await getDoc(ref);
        if (snap.exists()) before = { id: snap.id, ...snap.data() };
      } catch { /* best effort */ }
      await deleteDoc(ref);
      auditService.log({
        module: AUDIT_MODULES.EXTERNAL_DRIVER,
        action: AUDIT_ACTIONS.DELETE,
        recordId: driverId,
        recordLabel: extDriverLabel(before),
        before,
        after: null,
        tags: [AUDIT_TAGS.ALL, AUDIT_TAGS.DRIVER_CHANGES, AUDIT_TAGS.DELETIONS],
        companyId: before?.companyId,
        meta: { driverId, external: true }
      });
    } catch (error) {
      console.error('Error deleting external driver:', error);
      throw error;
    }
  }
};